import {
  AlertCircle,
  ArrowRightLeft,
  Check,
  MapPin,
  Users,
} from "lucide-react";
import { useEffect, useId, useMemo, useState } from "react";
import { toast } from "sonner";

import { Button } from "components/ui/button";
import { Label } from "components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "components/ui/sheet";
import { Textarea } from "components/ui/textarea";
import { cn } from "utils";

import { DEFAULT_TRACK_COLOR } from "../event-experience/AgendaSchedule";
import type { Track } from "../event-experience/schemas";
import { useDirectory } from "../event-experience/use-event-content";
import { MAX_REASON_LENGTH, type TrackSwitchRequest } from "./schemas";
import type { OwnRequestState, TrackSwitchWrites } from "./use-track-switch";

/**
 * The attendee's side of a table switch (TNG-92): a button that opens the
 * request sheet, or, while a request is open, what they asked for and a way
 * to change or withdraw it.
 *
 * `accepting` is false once the organiser has switched the feature off or the
 * event has ended. An open request still shows then, because the organiser can
 * still answer it, but no new one can be made or changed.
 */
export function TrackSwitchControl({
  tracks,
  currentTrackId,
  request,
  writes,
  accepting,
  eventId,
}: {
  tracks: Track[];
  currentTrackId?: string;
  request: OwnRequestState;
  writes: TrackSwitchWrites;
  accepting: boolean;
  eventId: string;
}) {
  const [open, setOpen] = useState(false);
  if (request.status !== "ready") return null;
  const pending =
    request.request?.status === "OPEN" ? request.request : undefined;
  const hasTable = currentTrackId !== undefined;
  // Other tables only: asking for the one you have is refused by the rules.
  const choices = tracks.filter((track) => track.id !== currentTrackId);

  if (!pending && (!accepting || choices.length === 0)) return null;

  return (
    <div data-testid="track-switch">
      {pending ? (
        <PendingRequest
          request={pending}
          tracks={tracks}
          hasTable={hasTable}
          canChange={accepting && choices.length > 0}
          saving={writes.saving}
          onChange={() => setOpen(true)}
          onWithdraw={() =>
            void writes.withdraw().then((ok) => {
              if (ok) toast.success("Request withdrawn.");
            })
          }
        />
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="w-full sm:w-auto"
          onClick={() => setOpen(true)}
          data-testid="track-switch-open"
        >
          <ArrowRightLeft className="h-4 w-4" />
          {hasTable ? "Request a theme table switch" : "Request a theme table"}
        </Button>
      )}
      {writes.error && (
        <p
          role="alert"
          className="mt-2 flex items-start gap-2 text-sm text-foreground"
        >
          <AlertCircle
            className="mt-0.5 h-4 w-4 shrink-0 text-destructive-text dark:text-hb-red-text"
            aria-hidden
          />
          {writes.error}
        </p>
      )}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="bottom"
          className="mx-auto max-h-[90dvh] max-w-xl rounded-t-lg sm:bottom-4 sm:rounded-lg"
          data-testid="track-switch-sheet"
        >
          {open && (
            <RequestForm
              eventId={eventId}
              choices={choices}
              hasTable={hasTable}
              initial={pending}
              saving={writes.saving}
              error={writes.error}
              onSubmit={(toTrackId, reason) =>
                void writes
                  .submit({
                    toTrackId,
                    fromTrackId: currentTrackId ?? null,
                    reason,
                  })
                  .then((ok) => {
                    if (!ok) return;
                    setOpen(false);
                    toast.success(
                      "Request sent. The organisers will let you know.",
                    );
                  })
              }
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function PendingRequest({
  request,
  tracks,
  hasTable,
  canChange,
  saving,
  onChange,
  onWithdraw,
}: {
  request: TrackSwitchRequest;
  tracks: Track[];
  hasTable: boolean;
  canChange: boolean;
  saving: boolean;
  onChange: () => void;
  onWithdraw: () => void;
}) {
  const table =
    tracks.find((track) => track.id === request.toTrackId)?.name ??
    "another theme table";
  return (
    <div
      className="space-y-3 rounded-md border border-border bg-background/50 px-3 py-3"
      data-testid="track-switch-pending"
    >
      <p className="text-sm text-foreground">
        {hasTable ? "You asked to move to " : "You asked for a seat at "}
        <strong
          className="font-semibold"
          data-testid="track-switch-pending-table"
        >
          {table}
        </strong>
        . The organisers will let you know.
      </p>
      {request.reason && (
        <p className="whitespace-pre-line break-words text-sm text-muted-foreground">
          <span className="font-medium">Your reason: </span>
          {request.reason}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {canChange && (
          <Button
            variant="outline"
            size="sm"
            onClick={onChange}
            disabled={saving}
            data-testid="track-switch-change"
          >
            Change request
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={onWithdraw}
          disabled={saving}
          data-testid="track-switch-withdraw"
        >
          Withdraw
        </Button>
      </div>
    </div>
  );
}

function RequestForm({
  eventId,
  choices,
  hasTable,
  initial,
  saving,
  error,
  onSubmit,
}: {
  eventId: string;
  choices: Track[];
  hasTable: boolean;
  initial?: TrackSwitchRequest;
  saving: boolean;
  error: string | null;
  onSubmit: (toTrackId: string, reason: string) => void;
}) {
  const [toTrackId, setToTrackId] = useState(
    initial && choices.some((track) => track.id === initial.toTrackId)
      ? initial.toTrackId
      : "",
  );
  const [reason, setReason] = useState(initial?.reason ?? "");
  const reasonId = useId();
  const seated = useSeatedCounts(eventId);

  // A table deleted while the sheet is open is no longer a choice.
  useEffect(() => {
    if (toTrackId && !choices.some((track) => track.id === toTrackId)) {
      setToTrackId("");
    }
  }, [choices, toTrackId]);

  const tooLong = reason.length > MAX_REASON_LENGTH;

  return (
    <form
      className="space-y-5 pt-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (toTrackId && !tooLong) onSubmit(toTrackId, reason);
      }}
    >
      <SheetHeader className="pr-10">
        <SheetTitle>
          {hasTable ? "Request a theme table switch" : "Request a theme table"}
        </SheetTitle>
        <SheetDescription>
          Choose the theme table you would like. The organisers decide, and you
          will find their answer in your notifications.
        </SheetDescription>
      </SheetHeader>

      <fieldset className="space-y-2">
        <legend className="mb-2 text-sm font-medium text-foreground">
          Theme table
        </legend>
        <div role="radiogroup" aria-label="Theme table" className="space-y-2">
          {choices.map((track) => {
            const selected = track.id === toTrackId;
            const count = seated ? (seated.get(track.id) ?? 0) : undefined;
            return (
              <button
                key={track.id}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setToTrackId(track.id)}
                data-testid={`track-switch-choice-${track.id}`}
                className={cn(
                  "flex min-h-11 w-full items-center gap-3 rounded-md border px-3 py-2 text-left transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring",
                  selected
                    ? "border-lit bg-lit/10"
                    : "border-border hover:bg-muted",
                )}
              >
                <span
                  className="inline-block h-3 w-3 shrink-0 rounded-full"
                  style={{
                    backgroundColor: track.color ?? DEFAULT_TRACK_COLOR,
                  }}
                  aria-hidden
                />
                <span className="min-w-0 flex-1">
                  <span className="block font-medium text-foreground">
                    {track.name}
                  </span>
                  <span className="flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                    {track.room?.trim() && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="h-3 w-3" aria-hidden />
                        {track.room}
                      </span>
                    )}
                    {count !== undefined && (
                      <span className="inline-flex items-center gap-1">
                        <Users className="h-3 w-3" aria-hidden />
                        {count === 1 ? "1 person" : `${count} people`}
                      </span>
                    )}
                  </span>
                </span>
                {selected && (
                  <Check className="h-4 w-4 shrink-0 text-lit" aria-hidden />
                )}
              </button>
            );
          })}
        </div>
      </fieldset>

      <div className="space-y-2">
        <Label htmlFor={reasonId}>Reason (optional)</Label>
        <Textarea
          id={reasonId}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Why this theme table? The organisers see what you write."
          aria-describedby={`${reasonId}-count`}
          data-testid="track-switch-reason"
        />
        <p
          id={`${reasonId}-count`}
          className={cn(
            "flex items-center justify-end gap-1 text-xs",
            tooLong ? "font-semibold text-foreground" : "text-muted-foreground",
          )}
        >
          {tooLong && (
            <AlertCircle
              className="h-3.5 w-3.5 text-destructive-text dark:text-hb-red-text"
              aria-hidden
            />
          )}
          {reason.length}/{MAX_REASON_LENGTH}
        </p>
      </div>

      {error && (
        <p
          role="alert"
          className="flex items-start gap-2 text-sm text-foreground"
        >
          <AlertCircle
            className="mt-0.5 h-4 w-4 shrink-0 text-destructive-text dark:text-hb-red-text"
            aria-hidden
          />
          {error}
        </p>
      )}

      <Button
        variant="cta"
        type="submit"
        className="w-full"
        disabled={!toTrackId || tooLong || saving}
        data-testid="track-switch-submit"
      >
        {saving ? "Sending…" : initial ? "Update request" : "Send request"}
      </Button>
    </form>
  );
}

/**
 * How many people sit at each table, from the member-readable directory.
 * Tables have no capacity (the TNG-92 decision), so this is the only size an
 * attendee can weigh. Null while loading or if the read fails.
 */
function useSeatedCounts(eventId: string): Map<string, number> | null {
  const directory = useDirectory(eventId);
  return useMemo(() => {
    if (directory.status !== "success") return null;
    const counts = new Map<string, number>();
    for (const person of directory.data) {
      if (person.trackId) {
        counts.set(person.trackId, (counts.get(person.trackId) ?? 0) + 1);
      }
    }
    return counts;
  }, [directory]);
}
